import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import "./LudDocuments.css";
import { Context } from "../../context/Context";
import { useAccess } from "../../access/hooks/useAccess";
import { Button, NoticeBox, RefreshCcw, Upload, X } from "../ui";
import { Loading } from "../loading/Loading";
import RestaurantSelector from "./components/RestaurantSelector/RestaurantSelector";
import RestaurantTabs from "./components/RestaurantTabs/RestaurantTabs";
import CategoriesSidebar from "./components/CategoriesSidebar/CategoriesSidebar";
import MetricsGrid from "./components/MetricsGrid/MetricsGrid";
import AlertsList from "./components/AlertsList/AlertsList";
import UploadZone from "./components/UploadZone/UploadZone";
import AdminOverview from "./components/AdminOverview/AdminOverview";
import OrganizationDocuments from "./sections/OrganizationDocuments/OrganizationDocuments";
import AppContracts from "./sections/AppContracts/AppContracts";
import AccountStatements from "./sections/AccountStatements/AccountStatements";
import UpdateRequests from "./sections/UpdateRequests/UpdateRequests";
import DailyReports from "./sections/DailyReports/DailyReports";
import MonthlyReports from "./sections/MonthlyReports/MonthlyReports";
import ItemSales from "./sections/ItemSales/ItemSales";
import { CATEGORIES, TEXT } from "./utils/ludDocuments.constants";
import {
  getCategoryView,
  getText,
  mapLudRecordToRestaurant,
} from "./utils/ludDocuments.helpers";
import {
  createLudContractAPI,
  createLudUpdateRequestAPI,
  updateLudContractAPI,
  getAllLudDocumentsAPI,
  getLudDocumentsRestaurantsAPI,
  getStoreLudDocumentsAPI,
  uploadOrganizationDocumentAPI,
} from "../../utils/api/ludDocuments";

const todayValue = () => new Date().toISOString().slice(0, 10);

export const LudDocuments = () => {
  const { lang = "ar", storeAccess } = useContext(Context);
  const { can, has } = useAccess();
  // dm sees categories 4-5, menuSp sees 6, everyone else all of them
  // (see access/modules/lud-documents.js).
  const visibleCategories = useMemo(
    () =>
      CATEGORIES.filter((_, index) =>
        can("ludDocument.category.view", { categoryIndex: index }),
      ),
    [can],
  );
  const t = (key) => getText(TEXT, key, lang);
  const token = localStorage.getItem("auth-token");
  const [restaurants, setRestaurants] = useState([]);
  const [restaurantId, setRestaurantId] = useState("");
  const [categoryKey, setCategoryKey] = useState("orgdocs");
  const [view, setView] = useState("restaurant");
  const [uploadTarget, setUploadTarget] = useState(null);
  const [loadState, setLoadState] = useState({ status: "loading", error: "" });
  const [uploading, setUploading] = useState(false);
  const [notice, setNotice] = useState(null);

  const canPermanentDelete = has("ludDocument.permanentDelete");
  // Derived on every render: a memo with no dependencies kept the value from
  // the first render even if the user's access loaded later.
  const isAdmin = can("ludDocument.admin.view");

  // Failures anywhere on the page are shown here instead of only in the console.
  const reportError = useCallback(
    (error, fallback) => {
      console.error(error);
      setNotice({
        text:
          fallback ||
          (lang === "ar" ? "تعذّر تنفيذ العملية" : "The action failed"),
        detail: error?.message || "",
      });
    },
    [lang],
  );
  useEffect(() => {
    const isAllowed = visibleCategories.some(
      (category) => category.key === categoryKey,
    );

    if (!isAllowed) {
      setCategoryKey(visibleCategories[0]?.key || "");
      setUploadTarget(null);
    }
  }, [visibleCategories, categoryKey]);

  // Context rebuilds storeAccess as a new array on each render; key on its
  // contents so the list refetches only when the allowed stores really change.
  const storeAccessKey = JSON.stringify(storeAccess || []);
  const loadRestaurants = useCallback(async () => {
    const [items, records] = await Promise.all([
      getLudDocumentsRestaurantsAPI(token, storeAccess),
      getAllLudDocumentsAPI(token, storeAccess),
    ]);

    const mapped = items.map((item) => {
      const record = records.find((document) => {
        const storeId =
          document.storeCode || document.store?._id || document.store;
        return String(storeId || "") === String(item.id || item._id || "");
      });

      return mapLudRecordToRestaurant(item, record || null);
    });

    setRestaurants(mapped);
    setRestaurantId((current) => current || mapped[0]?.id || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, storeAccessKey]);

  const loadRestaurant = useCallback(
    async (storeId) => {
      if (!storeId) return;
      const record = await getStoreLudDocumentsAPI(token, storeId);
      setRestaurants((current) => {
        const summary = current.find((item) => item.id === storeId) || {};
        const mapped = mapLudRecordToRestaurant(summary, record);
        return current.map((item) => (item.id === storeId ? mapped : item));
      });
    },
    [token],
  );

  const initialLoad = useCallback(() => {
    setLoadState({ status: "loading", error: "" });
    loadRestaurants()
      .then(() => setLoadState({ status: "ready", error: "" }))
      .catch((error) => {
        console.error(error);
        setLoadState({ status: "error", error: error?.message || "" });
      });
  }, [loadRestaurants]);

  useEffect(() => {
    initialLoad();
  }, [initialLoad]);
  useEffect(() => {
    setUploadTarget(null);
    loadRestaurant(restaurantId).catch((error) =>
      reportError(
        error,
        lang === "ar"
          ? "تعذّر تحميل مستندات هذا المطعم"
          : "Could not load this restaurant's documents",
      ),
    );
    // reportError/lang only affect the message text; refetch on restaurant change only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId, loadRestaurant]);

  const restaurant = useMemo(
    () =>
      restaurants.find((item) => item.id === restaurantId) || restaurants[0],
    [restaurants, restaurantId],
  );
  // Writes (upload, edit, delete, sync) need ludDocument.manage for this restaurant.
  const canManage = can("ludDocument.manage", { restaurantId: restaurant?.id });
  const category =
    CATEGORIES.find((item) => item.key === categoryKey) || CATEGORIES[0];
  const categoryView = useMemo(
    () =>
      restaurant
        ? getCategoryView(categoryKey, restaurant, lang)
        : { count: 0, alerts: [], metrics: [] },
    [categoryKey, restaurant, lang],
  );

  const uploadEnabled = useMemo(() => {
    if (!uploadTarget) return false;
    if (categoryKey === "orgdocs") return Boolean(uploadTarget.documentKey);
    if (categoryKey === "contracts") return Boolean(uploadTarget.app);
    if (categoryKey === "updates") return Boolean(uploadTarget.type);
    return false;
  }, [categoryKey, uploadTarget]);

  const updateRestaurant = (updater) =>
    setRestaurants((current) =>
      current.map((item) =>
        item.id === restaurant?.id ? updater(item) : item,
      ),
    );
  const refreshRestaurant = useCallback(
    () => loadRestaurant(restaurant?.id),
    [loadRestaurant, restaurant?.id],
  );

  const handleJump = (id, key) => {
    setRestaurantId(id);
    setCategoryKey(key);
    setView("restaurant");
  };

  const handleFiles = async (files) => {
    const file = files?.[0];
    if (!file || !restaurant) return;

    if (!uploadEnabled || uploading) return;

    const target = uploadTarget;

    setUploading(true);
    try {
      if (categoryKey === "orgdocs") {
        await uploadOrganizationDocumentAPI(
          token,
          restaurant.id,
          target.documentKey,
          file,
        );
      } else if (categoryKey === "contracts") {
        const existingContract = restaurant.contracts?.[target.app];

        if (existingContract?.id) {
          await updateLudContractAPI(
            token,
            restaurant.id,
            existingContract.id,
            {
              file,
              app: target.app,
              startDate: todayValue(),
              status: "active",
            },
          );
        } else {
          await createLudContractAPI(token, restaurant.id, {
            file,
            app: target.app,
            startDate: todayValue(),
          });
        }
      } else if (categoryKey === "updates") {
        await createLudUpdateRequestAPI(token, restaurant.id, {
          file,
          type: target.type,
        });
      }
      await refreshRestaurant();
      setUploadTarget(null);
    } catch (error) {
      reportError(
        error,
        lang === "ar" ? "تعذّر رفع الملف" : "The file could not be uploaded",
      );
    } finally {
      setUploading(false);
    }
  };

  const renderSection = () => {
    if (!restaurant) return null;
    const props = {
      restaurant,
      lang,
      updateRestaurant,
      token,
      refreshRestaurant,
      uploadTarget,
      onUploadTargetChange: setUploadTarget,
      canPermanentDelete,
      // writes follow ludDocument.manage for THIS restaurant (viewing the page or its overview never implies it)
      canManage,
      onError: reportError,
    };
    switch (categoryKey) {
      case "contracts":
        return <AppContracts {...props} />;
      case "statements":
        return <AccountStatements {...props} />;
      case "updates":
        return <UpdateRequests {...props} />;
      case "daily":
        return <DailyReports {...props} />;
      case "monthly":
        return <MonthlyReports {...props} />;
      case "itemsales":
        return <ItemSales {...props} />;
      default:
        return <OrganizationDocuments {...props} />;
    }
  };

  const dir = lang === "ar" ? "rtl" : "ltr";

  if (!restaurant) {
    if (loadState.status === "loading") {
      return (
        <div className="lud-documents" dir={dir}>
          <Loading />
        </div>
      );
    }

    return (
      <div className="lud-documents" dir={dir}>
        <div className="lud-documents__state">
          {loadState.status === "error" ? (
            <NoticeBox
              variant="danger"
              title={
                lang === "ar"
                  ? "تعذّر تحميل المستندات"
                  : "Documents could not be loaded"
              }
            >
              {loadState.error && <p>{loadState.error}</p>}
              <Button
                variant="danger"
                appearance="outline"
                icon={<RefreshCcw size={15} />}
                onClick={initialLoad}
              >
                {lang === "ar" ? "إعادة المحاولة" : "Retry"}
              </Button>
            </NoticeBox>
          ) : (
            <NoticeBox
              variant="neutral"
              title={
                lang === "ar" ? "لا توجد مطاعم بعد" : "No restaurants yet"
              }
            >
              {lang === "ar"
                ? "ستظهر مستندات المطاعم هنا بمجرد إضافتها."
                : "Restaurant documents will appear here once they are added."}
            </NoticeBox>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="lud-documents" dir={dir}>
      {notice && (
        <div className="lud-documents__notice" role="alert">
          <NoticeBox variant="danger" title={notice.text}>
            {notice.detail && <p>{notice.detail}</p>}
          </NoticeBox>
          <Button
            variant="neutral"
            appearance="ghost"
            size="sm"
            icon={<X size={16} />}
            aria-label={lang === "ar" ? "إغلاق التنبيه" : "Dismiss"}
            title={lang === "ar" ? "إغلاق التنبيه" : "Dismiss"}
            onClick={() => setNotice(null)}
          />
        </div>
      )}
      {view === "admin" ? (
        <AdminOverview
          restaurants={restaurants}
          lang={lang}
          onBack={() => setView("restaurant")}
          onJump={handleJump}
        />
      ) : (
        <>
          <div className="lud-documents__filter-card">
            <RestaurantSelector
              restaurants={restaurants}
              value={restaurant.id}
              onChange={setRestaurantId}
              lang={lang}
            />
          </div>
          <RestaurantTabs
            restaurants={restaurants}
            selectedId={restaurant.id}
            onSelect={setRestaurantId}
            isAdmin={isAdmin}
            onAdmin={() => setView("admin")}
            lang={lang}
          />
          <div className="lud-documents__workspace">
            <CategoriesSidebar
              categories={visibleCategories}
              selectedKey={categoryKey}
              restaurant={restaurant}
              onSelect={(key) => {
                setCategoryKey(key);
                setUploadTarget(null);
              }}
              lang={lang}
            />
            <main className="lud-documents__content">
              <header className="lud-documents__header">
                <div>
                  <h1>{category.label[lang] || category.label.ar}</h1>
                  <p>
                    {category.label[lang === "ar" ? "en" : "ar"]} •{" "}
                    {restaurant.name}
                  </p>
                </div>
                <span className="lud-documents__count">
                  {categoryView.count}
                </span>
              </header>
              {isAdmin&&<AlertsList alerts={categoryView.alerts} lang={lang} />}
              {isAdmin&&<MetricsGrid metrics={categoryView.metrics} />}
              {renderSection()}
              {!["statements", "daily", "monthly", "itemsales"].includes(
                categoryKey,
              ) &&canManage&& (
                <UploadZone
                  label={
                    uploading
                      ? lang === "ar"
                        ? "جارٍ الرفع…"
                        : "Uploading…"
                      : t("uploadZone")
                  }
                  icon={<Upload size={20} />}
                  onFiles={handleFiles}
                  disabled={!uploadEnabled || uploading}
                  busy={uploading}
                  lang={lang}
                />
              )}
            </main>
          </div>
        </>
      )}
    </div>
  );
};

export default LudDocuments;
